import { NavigateFunction } from "react-router-dom";

import { ApiService, CaseAdmin } from "pwapi";

import { CaseAdminDetail } from "../../reducers/admin/adminCaseReducer";
import { AsyncSagaAction, createAsyncSaga } from "../../utils/asyncActions";
import {
  ADMIN_CASE_REQUEST,
  ADMIN_UPDATE_CASE_REQUEST,
  ADMIN_DELETE_CASE_REQUEST,
  ADMIN_RESET_ERROR_MESSAGE,
  ADMIN_RESET_DELETION_STATUS,
  ADMIN_CREATE_QAREVIEW_REQUEST,
  ADMIN_UPDATE_QAREVIEW_REQUEST,
  ADMIN_UPDATE_INTEGRATIONS_REQUEST,
  ADMIN_REASSIGN_ACCESSION_NUMBER_REQUEST,
  ADMIN_REASSIGN_ACCESSION_NUMBER_SUCCESS,
  ADMIN_REASSIGN_ACCESSION_NUMBER_FAILURE,
  ADMIN_CASE_FAILURE,
  ADMIN_CASE_SUCCESS,
  ADMIN_COMPLETE_CASE_DATA_ENTRY_REQUEST,
  ADMIN_COMPLETE_CASE_DATA_ENTRY_SUCCESS,
  ADMIN_COMPLETE_CASE_DATA_ENTRY_FAILURE,
  ADMIN_PRINT_LAB_ORDERS_SLIDES_REQUEST,
  ADMIN_PRINT_LAB_ORDERS_SLIDES_SUCCESS,
  ADMIN_PRINT_LAB_ORDERS_SLIDES_FAILURE,
  ADMIN_HOLD_CASE_DATA_ENTRY_FAILURE,
  ADMIN_HOLD_CASE_DATA_ENTRY_REQUEST,
  ADMIN_HOLD_CASE_DATA_ENTRY_SUCCESS,
} from "../adminConstants";

export interface CaseAdminRelationships {
  active_assignment_id?: number;
  ordering_doctor_id?: number;
  lab_id?: number;
  accessioning_lab_id?: number;
  patient_id?: number;
}

export const requestAdminCase = createAsyncSaga(
  ADMIN_CASE_REQUEST,
  ADMIN_CASE_SUCCESS,
  ADMIN_CASE_FAILURE,
  (id) => `admin/cases/${id}`,
  (id) => ApiService.retrieveAdminCase({ id })
) as AsyncSagaAction<number>;

export const saveAdminCase = (
  payload: Partial<CaseAdmin & CaseAdminRelationships>
) => {
  return {
    type: ADMIN_UPDATE_CASE_REQUEST,
    payload,
  };
};

export const deleteAdminCase = (payload: {
  adminCase: CaseAdminDetail;
  navigate: NavigateFunction;
  historyState?: { label: string; prevUrl: string };
}) => {
  return {
    type: ADMIN_DELETE_CASE_REQUEST,
    payload,
  };
};

export const resetCaseErrorMessage = () => {
  return {
    type: ADMIN_RESET_ERROR_MESSAGE,
  };
};

export const resetDeletionStatus = () => {
  return {
    type: ADMIN_RESET_DELETION_STATUS,
  };
};

export const createQAReview = (values: {
  reviewed: boolean;
  note: string;
  caseId: number;
}) => {
  return {
    type: ADMIN_CREATE_QAREVIEW_REQUEST,
    payload: values,
  };
};

export const updateQAReview = ({ id, note }: { id: number; note: string }) => {
  return {
    type: ADMIN_UPDATE_QAREVIEW_REQUEST,
    payload: { id, requestBody: { note } },
  };
};

type clinicIntegrationsUpdate = {
  case_id: number;
  clinic_id?: number | null;
  ordering_doctor_id?: number | null;
} & {
  [key: string]: number | boolean | string;
};

export const updateIntegrations = (payload: clinicIntegrationsUpdate) => {
  return {
    type: ADMIN_UPDATE_INTEGRATIONS_REQUEST,
    payload,
  };
};

type ReassignAccessionArgs = Parameters<
  typeof ApiService.reassignAccessionNumberAdminCase
>[0];
export const reassignAccessionNumberAdminCase = createAsyncSaga(
  ADMIN_REASSIGN_ACCESSION_NUMBER_REQUEST,
  ADMIN_REASSIGN_ACCESSION_NUMBER_SUCCESS,
  ADMIN_REASSIGN_ACCESSION_NUMBER_FAILURE,
  ({ id }) => `/api/admin/cases/${id}/reassign_accession_number/`,
  ApiService.reassignAccessionNumberAdminCase
) as AsyncSagaAction<ReassignAccessionArgs>;

type CompleteDataEntryArgs = Parameters<
  typeof ApiService.completeDataEntryAdminCase
>[0];
export const completeDataEntry = createAsyncSaga(
  ADMIN_COMPLETE_CASE_DATA_ENTRY_REQUEST,
  ADMIN_COMPLETE_CASE_DATA_ENTRY_SUCCESS,
  ADMIN_COMPLETE_CASE_DATA_ENTRY_FAILURE,
  ({ id }) => `/api/admin/cases/${id}/complete_data_entry/`,
  ApiService.completeDataEntryAdminCase
) as AsyncSagaAction<CompleteDataEntryArgs>;

type HoldDataEntryArgs = Parameters<
  typeof ApiService.holdDataEntryAdminCase
>[0];
export const holdDataEntry = createAsyncSaga(
  ADMIN_HOLD_CASE_DATA_ENTRY_REQUEST,
  ADMIN_HOLD_CASE_DATA_ENTRY_SUCCESS,
  ADMIN_HOLD_CASE_DATA_ENTRY_FAILURE,
  ({ id }) => `/api/admin/cases/${id}/hold_data_entry/`,
  ApiService.holdDataEntryAdminCase
) as AsyncSagaAction<HoldDataEntryArgs>;

export const printLabOrdersSlides = createAsyncSaga(
  ADMIN_PRINT_LAB_ORDERS_SLIDES_REQUEST,
  ADMIN_PRINT_LAB_ORDERS_SLIDES_SUCCESS,
  ADMIN_PRINT_LAB_ORDERS_SLIDES_FAILURE,
  ({ id }) => `/api/admin/cases/${id}/print_lab_orders_slides/`,
  ApiService.printLabOrdersSlidesAdminCase
) as AsyncSagaAction<
  Parameters<typeof ApiService.printLabOrdersSlidesAdminCase>[0]
>;
